import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../utils/api";
import type { PdsProjectListItem } from "types/pds";

interface PdsProjectSelectorProps {
  projectId: string;
  onLinked?: (uuid: string) => void;
}

const ENTRIES_PER_PAGE = 20;

const PdsProjectSelector = ({
  projectId,
  onLinked,
}: PdsProjectSelectorProps) => {
  const { t } = useTranslation();
  const [pdsProjects, setPdsProjects] = useState<PdsProjectListItem[]>([]);
  const [linkedIds, setLinkedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [page, setPage] = useState(0);
  const [totalHitCount, setTotalHitCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [isSearching, setIsSearching] = useState(false);
  const [selectedProject, setSelectedProject] =
    useState<PdsProjectListItem | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentSearchRef = useRef("");

  const hasMore = pdsProjects.length < totalHitCount;

  const hasLoadedOnce = useRef(false);

  const fetchProjects = useCallback(
    async (
      pageNum: number,
      search: string,
      append: boolean,
      fetchLinked: boolean,
    ) => {
      if (append) {
        setIsLoadingMore(true);
      } else if (!hasLoadedOnce.current) {
        setIsLoading(true);
      } else {
        setIsSearching(true);
      }
      setError(null);

      try {
        const pdsPromise = api.fetchPdsProjects(
          pageNum,
          ENTRIES_PER_PAGE,
          search,
        );
        const linkedPromise = fetchLinked
          ? api.fetchLinkedPdsProjectIds()
          : Promise.resolve(null);

        const [pdsData, linkedIdsData] = await Promise.all([
          pdsPromise,
          linkedPromise,
        ]);

        // Ignore stale responses
        if (search !== currentSearchRef.current) return;

        setTotalHitCount(pdsData.totalHitCount);

        if (append) {
          setPdsProjects((prev) => [...prev, ...(pdsData.resultList || [])]);
        } else {
          setPdsProjects(pdsData.resultList || []);
        }

        if (linkedIdsData) {
          setLinkedIds(new Set(linkedIdsData));
        }

        hasLoadedOnce.current = true;
      } catch (err) {
        console.error("Error fetching PDS projects:", err);
        setError(
          t("failed_to_load_pds_projects", "Failed to load PDS projects"),
        );
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
        setIsSearching(false);
      }
    },
    [t],
  );

  // Initial load
  useEffect(() => {
    fetchProjects(0, "", false, true);
  }, [fetchProjects]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    currentSearchRef.current = searchQuery;
    setPage(0);

    debounceRef.current = setTimeout(() => {
      fetchProjects(0, searchQuery, false, false);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (isLoadingMore || !hasMore) return;

      const target = e.currentTarget;
      const scrollBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      if (scrollBottom < 40) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchProjects(nextPage, searchQuery, true, false);
      }
    },
    [isLoadingMore, hasMore, page, searchQuery, fetchProjects],
  );

  const handleLink = async () => {
    if (!selectedUuid) return;

    setIsLinking(true);
    try {
      await api.linkPdsProject(projectId, selectedUuid);
      toast.success(
        t("pds_project_linked", "PDS project linked successfully"),
      );
      if (onLinked) {
        onLinked(selectedUuid);
      }
    } catch (err) {
      console.error("Error linking PDS project:", err);
      toast.error(
        t("failed_to_link_pds_project", "Failed to link PDS project"),
      );
    } finally {
      setIsLinking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
          <span className="text-sm text-gray-500">
            {t("loading_pds_projects", "Loading PDS projects...")}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <img src="/pds-logo.png" alt="PDS" className="h-6 w-auto" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t("link_pds_project", "Link PDS Project")}
          </h3>
        </div>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          {t("not_linked", "Not Linked")}
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        {t(
          "pds_link_description",
          "Select a PDS project to link with this project. This will associate PDS project data with the current project.",
        )}
      </p>

      {/* Dropdown */}
      <div ref={dropdownRef} className="relative mb-4">
        {/* Trigger button */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
          className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <span className={selectedProject ? "text-gray-900" : "text-gray-400"}>
            {selectedProject
              ? `${selectedProject.nummer} — ${selectedProject.bezeichnung}`
              : t("select_pds_project", "-- Select a PDS project --")}
          </span>
          <svg
            className={`h-4 w-4 text-gray-500 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* Dropdown panel */}
        {isOpen && (
          <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {/* Search input */}
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                    clipRule="evenodd"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t(
                    "search_pds_projects",
                    "Search by number or name...",
                  )}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Scrollable list */}
            <div
              ref={listRef}
              onScroll={handleScroll}
              className="max-h-64 overflow-y-auto"
            >
              {isSearching ? (
                <div className="flex items-center justify-center py-6">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
                  <span className="text-xs text-gray-500 ml-2">
                    {t("searching", "Searching...")}
                  </span>
                </div>
              ) : pdsProjects.length === 0 ? (
                <p className="text-sm text-gray-400 p-4 italic text-center">
                  {searchQuery.trim()
                    ? t("no_search_results", "No results found.")
                    : t("no_pds_projects_found", "No PDS projects found.")}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {pdsProjects.map((project) => {
                    const isAlreadyLinked = linkedIds.has(project.uuid);
                    const isSelected = selectedUuid === project.uuid;

                    return (
                      <li key={project.uuid}>
                        <button
                          type="button"
                          disabled={isAlreadyLinked || isLinking}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedUuid(null);
                              setSelectedProject(null);
                            } else {
                              setSelectedUuid(project.uuid);
                              setSelectedProject(project);
                            }
                            setIsOpen(false);
                            setSearchQuery("");
                          }}
                          className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                            isAlreadyLinked
                              ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                              : isSelected
                                ? "bg-blue-50 text-blue-900"
                                : "hover:bg-gray-50 text-gray-900 cursor-pointer"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  isSelected
                                    ? "border-blue-500 bg-blue-500"
                                    : isAlreadyLinked
                                      ? "border-gray-300 bg-gray-100"
                                      : "border-gray-300"
                                }`}
                              >
                                {isSelected && (
                                  <svg
                                    className="w-2.5 h-2.5 text-white"
                                    fill="currentColor"
                                    viewBox="0 0 12 12"
                                  >
                                    <path d="M10.28 2.28L4.5 8.06 1.72 5.28a.75.75 0 00-1.06 1.06l3.5 3.5a.75.75 0 001.06 0l6.5-6.5a.75.75 0 00-1.06-1.06z" />
                                  </svg>
                                )}
                              </span>
                              <span className="font-medium truncate">
                                {project.nummer}
                              </span>
                              <span className="text-gray-500 truncate">
                                — {project.bezeichnung}
                              </span>
                            </div>
                            {isAlreadyLinked && (
                              <span className="flex-shrink-0 text-xs text-gray-400 ml-2">
                                {t("already_linked", "already linked")}
                              </span>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Loading more indicator */}
              {isLoadingMore && (
                <div className="flex items-center justify-center py-3 border-t border-gray-100">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
                  <span className="text-xs text-gray-500 ml-2">
                    {t("loading_more", "Loading more...")}
                  </span>
                </div>
              )}
            </div>

            {/* Dropdown footer */}
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
              <span className="text-xs text-gray-400">
                {t("showing_of", "Showing {{count}} of {{total}}", {
                  count: pdsProjects.length,
                  total: totalHitCount,
                })}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Link button */}
      <div className="flex justify-end">
        <button
          onClick={handleLink}
          disabled={!selectedUuid || isLinking}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            !selectedUuid || isLinking
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {isLinking
            ? t("linking", "Linking...")
            : t("link_project", "Link Project")}
        </button>
      </div>
    </div>
  );
};

export default PdsProjectSelector;
